export interface Translations {
  // Header
  welcome: string;
  subtitle: string;
  language: string;
  
  // Buttons
  uploadFile: string;
  startRecording: string;
  stopRecording: string;
  processing: string;
  
  // Transcription
  liveTranscription: string;
  previousTranscriptions: string;
  noTranscriptions: string;
  
  // Tooltips
  uploadAudioFile: string;
  recordWithMicrophone: string;
  playAudio: string;
  pause: string;
  downloadAudio: string;
  downloadTranscription: string;
  delete: string;
  decreaseTextSize: string;
  increaseTextSize: string;
  copyText: string;
  
  // Language names
  languages: {
    'fr-FR': string;
    'en-US': string;
    'de-DE': string;
    'nl-NL': string;
    'it-IT': string;
    'es-ES': string;
    'sv-SE': string;
    'pt-PT': string;
    'pl-PL': string;
    'ja-JP': string;
    'he-IL': string;
    'tr-TR': string;
  };

  // Placeholder messages
  placeholders: {
    'fr-FR': string;
    'en-US': string;
    'de-DE': string;
    'nl-NL': string;
    'it-IT': string;
    'es-ES': string;
    'sv-SE': string;
    'pt-PT': string;
    'pl-PL': string;
    'ja-JP': string;
    'he-IL': string;
    'tr-TR': string;
  };
  
  // Transcription types
  live: string;
  file: string;
  
  // Messages
  textCopied: string;
  cantReachServer: string;
  
  // Teleprompter
  teleprompter: string;
  waitingForTranscription: string;
  
  // Usage Analytics
  usageAnalytics: string;
  timeRange: string;
  days: string;

  // Dictation mode
  dictationModeOn: string;
  dictationModeOff: string;

  // TTS
  ttsTitle: string;
  ttsSubtitle: string;
}

export const translations: Record<string, Translations> = {
  'en-US': {
    welcome: 'Welcome to Voxist AI',
    subtitle: 'Speech AI Platform - ASR & TTS',
    language: 'Language',
    
    uploadFile: 'Upload File',
    startRecording: 'Start Recording',
    stopRecording: 'Stop Recording',
    processing: 'Processing...',
    
    liveTranscription: 'Live Transcription',
    previousTranscriptions: 'Previous Transcriptions',
    noTranscriptions: 'No transcriptions yet',
    
    uploadAudioFile: 'Upload audio file',
    recordWithMicrophone: 'Record with microphone',
    playAudio: 'Play Audio',
    pause: 'Pause',
    downloadAudio: 'Download Audio',
    downloadTranscription: 'Download Transcription',
    delete: 'Delete',
    decreaseTextSize: 'Decrease Text Size',
    increaseTextSize: 'Increase Text Size',
    copyText: 'Copy Text',
    
    languages: {
      'fr-FR': 'French',
      'en-US': 'English',
      'de-DE': 'German',
      'nl-NL': 'Dutch',
      'it-IT': 'Italian',
      'es-ES': 'Spanish',
      'sv-SE': 'Swedish',
      'pt-PT': 'Portuguese',
      'pl-PL': 'Polish',
      'ja-JP': 'Japanese',
      'he-IL': 'Hebrew',
      'tr-TR': 'Turkish'
    },

    placeholders: {
      'fr-FR': 'Select French and start speaking to see the transcription here...',
      'en-US': 'Select English and start speaking to see the transcription here...',
      'de-DE': 'Select German and start speaking to see the transcription here...',
      'nl-NL': 'Select Dutch and start speaking to see the transcription here...',
      'it-IT': 'Select Italian and start speaking to see the transcription here...',
      'es-ES': 'Select Spanish and start speaking to see the transcription here...',
      'sv-SE': 'Select Swedish and start speaking to see the transcription here...',
      'pt-PT': 'Select Portuguese and start speaking to see the transcription here...',
      'pl-PL': 'Select Polish and start speaking to see the transcription here...',
      'ja-JP': 'Select Japanese and start speaking to see the transcription here...',
      'he-IL': 'Select Hebrew and start speaking to see the transcription here...',
      'tr-TR': 'Select Turkish and start speaking to see the transcription here...'
    },
    
    live: 'Live',
    file: 'File',
    
    textCopied: 'Text copied to clipboard',
    cantReachServer: "Can't reach server",
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'Waiting for transcription...',
    
    usageAnalytics: 'Usage Analytics',
    timeRange: 'Time Range',
    days: 'days',

    dictationModeOn: 'Dictation mode ON - Speak punctuation (e.g., "period", "comma")',
    dictationModeOff: 'Dictation mode OFF - Punctuation generated automatically',

    // TTS
    ttsTitle: 'Text to Speech',
    ttsSubtitle: 'Convert text to speech'
  },
  
  'fr-FR': {
    welcome: 'Bienvenue sur Voxist AI',
    subtitle: 'Plateforme IA Vocale - ASR & TTS',
    language: 'Langue',
    
    uploadFile: 'Télécharger un fichier',
    startRecording: 'Commencer l\'enregistrement',
    stopRecording: 'Arrêter l\'enregistrement',
    processing: 'Traitement...',
    
    liveTranscription: 'Transcription en direct',
    previousTranscriptions: 'Transcriptions précédentes',
    noTranscriptions: 'Aucune transcription pour le moment',
    
    uploadAudioFile: 'Télécharger un fichier audio',
    recordWithMicrophone: 'Enregistrer avec le microphone',
    playAudio: 'Lire l\'audio',
    pause: 'Pause',
    downloadAudio: 'Télécharger l\'audio',
    downloadTranscription: 'Télécharger la transcription',
    delete: 'Supprimer',
    decreaseTextSize: 'Réduire la taille du texte',
    increaseTextSize: 'Augmenter la taille du texte',
    copyText: 'Copier le texte',
    
    languages: {
      'fr-FR': 'Français',
      'en-US': 'Anglais',
      'de-DE': 'Allemand',
      'nl-NL': 'Néerlandais',
      'it-IT': 'Italien',
      'es-ES': 'Espagnol',
      'sv-SE': 'Suédois',
      'pt-PT': 'Portugais',
      'pl-PL': 'Polonais',
      'ja-JP': 'Japonais',
      'he-IL': 'Hébreu',
      'tr-TR': 'Turc'
    },

    placeholders: {
      'fr-FR': 'Sélectionnez Français et commencez à parler pour voir la transcription ici...',
      'en-US': 'Sélectionnez Anglais et commencez à parler pour voir la transcription ici...',
      'de-DE': 'Sélectionnez Allemand et commencez à parler pour voir la transcription ici...',
      'nl-NL': 'Sélectionnez Néerlandais et commencez à parler pour voir la transcription ici...',
      'it-IT': 'Sélectionnez Italien et commencez à parler pour voir la transcription ici...',
      'es-ES': 'Sélectionnez Espagnol et commencez à parler pour voir la transcription ici...',
      'sv-SE': 'Sélectionnez Suédois et commencez à parler pour voir la transcription ici...',
      'pt-PT': 'Sélectionnez Portugais et commencez à parler pour voir la transcription ici...',
      'pl-PL': 'Sélectionnez Polonais et commencez à parler pour voir la transcription ici...',
      'ja-JP': 'Sélectionnez Japonais et commencez à parler pour voir la transcription ici...',
      'he-IL': 'Sélectionnez Hébreu et commencez à parler pour voir la transcription ici...',
      'tr-TR': 'Sélectionnez Turc et commencez à parler pour voir la transcription ici...'
    },
    
    live: 'Direct',
    file: 'Fichier',
    
    textCopied: 'Texte copié dans le presse-papiers',
    cantReachServer: 'Impossible de joindre le serveur',
    
    teleprompter: 'Téléprompteur',
    waitingForTranscription: 'En attente de transcription...',
    
    usageAnalytics: 'Analyses d\'utilisation',
    timeRange: 'Période',
    days: 'jours',

    dictationModeOn: 'Mode dictée ACTIF - Dictez la ponctuation (ex: "point", "virgule")',
    dictationModeOff: 'Mode dictée INACTIF - Ponctuation générée automatiquement',

    // TTS
    ttsTitle: 'Synthèse Vocale',
    ttsSubtitle: 'Convertir le texte en parole'
  },
  
  'de-DE': {
    welcome: 'Willkommen bei Voxist AI',
    subtitle: 'KI-Sprachplattform - ASR & TTS',
    language: 'Sprache',
    
    uploadFile: 'Datei hochladen',
    startRecording: 'Aufnahme starten',
    stopRecording: 'Aufnahme stoppen',
    processing: 'Verarbeitung...',
    
    liveTranscription: 'Live-Transkription',
    previousTranscriptions: 'Vorherige Transkriptionen',
    noTranscriptions: 'Noch keine Transkriptionen',
    
    uploadAudioFile: 'Audio-Datei hochladen',
    recordWithMicrophone: 'Mit Mikrofon aufnehmen',
    playAudio: 'Audio abspielen',
    pause: 'Pause',
    downloadAudio: 'Audio herunterladen',
    downloadTranscription: 'Transkription herunterladen',
    delete: 'Löschen',
    decreaseTextSize: 'Textgröße verringern',
    increaseTextSize: 'Textgröße erhöhen',
    copyText: 'Text kopieren',
    
    languages: {
      'fr-FR': 'Französisch',
      'en-US': 'Englisch',
      'de-DE': 'Deutsch',
      'nl-NL': 'Niederländisch',
      'it-IT': 'Italienisch',
      'es-ES': 'Spanisch',
      'sv-SE': 'Schwedisch',
      'pt-PT': 'Portugiesisch',
      'pl-PL': 'Polnisch',
      'ja-JP': 'Japanisch',
      'he-IL': 'Hebräisch',
      'tr-TR': 'Türkisch'
    },

    placeholders: {
      'fr-FR': 'Wählen Sie Französisch und sprechen Sie, um die Transkription hier zu sehen...',
      'en-US': 'Wählen Sie Englisch und sprechen Sie, um die Transkription hier zu sehen...',
      'de-DE': 'Wählen Sie Deutsch und sprechen Sie, um die Transkription hier zu sehen...',
      'nl-NL': 'Wählen Sie Niederländisch und sprechen Sie, um die Transkription hier zu sehen...',
      'it-IT': 'Wählen Sie Italienisch und sprechen Sie, um die Transkription hier zu sehen...',
      'es-ES': 'Wählen Sie Spanisch und sprechen Sie, um die Transkription hier zu sehen...',
      'sv-SE': 'Wählen Sie Schwedisch und sprechen Sie, um die Transkription hier zu sehen...',
      'pt-PT': 'Wählen Sie Portugiesisch und sprechen Sie, um die Transkription hier zu sehen...',
      'pl-PL': 'Wählen Sie Polnisch und sprechen Sie, um die Transkription hier zu sehen...',
      'ja-JP': 'Wählen Sie Japanisch und sprechen Sie, um die Transkription hier zu sehen...',
      'he-IL': 'Wählen Sie Hebräisch und sprechen Sie, um die Transkription hier zu sehen...',
      'tr-TR': 'Wählen Sie Türkisch und sprechen Sie, um die Transkription hier zu sehen...'
    },
    
    live: 'Live',
    file: 'Datei',
    
    textCopied: 'Text in die Zwischenablage kopiert',
    cantReachServer: 'Server nicht erreichbar',
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'Warte auf Transkription...',
    
    usageAnalytics: 'Nutzungsanalyse',
    timeRange: 'Zeitraum',
    days: 'Tage',

    dictationModeOn: 'Diktatmodus EIN - Sprechen Sie Satzzeichen (z.B. "Punkt", "Komma")',
    dictationModeOff: 'Diktatmodus AUS - Satzzeichen werden automatisch generiert',

    // TTS
    ttsTitle: 'Sprachsynthese',
    ttsSubtitle: 'Text in Sprache umwandeln'
  },
  
  'es-ES': {
    welcome: 'Bienvenido a Voxist AI',
    subtitle: 'Plataforma IA de Voz - ASR & TTS',
    language: 'Idioma',
    
    uploadFile: 'Subir archivo',
    startRecording: 'Comenzar grabación',
    stopRecording: 'Detener grabación',
    processing: 'Procesando...',
    
    liveTranscription: 'Transcripción en vivo',
    previousTranscriptions: 'Transcripciones anteriores',
    noTranscriptions: 'Aún no hay transcripciones',
    
    uploadAudioFile: 'Subir archivo de audio',
    recordWithMicrophone: 'Grabar con micrófono',
    playAudio: 'Reproducir audio',
    pause: 'Pausa',
    downloadAudio: 'Descargar audio',
    downloadTranscription: 'Descargar transcripción',
    delete: 'Eliminar',
    decreaseTextSize: 'Reducir tamaño del texto',
    increaseTextSize: 'Aumentar tamaño del texto',
    copyText: 'Copiar texto',
    
    languages: {
      'fr-FR': 'Francés',
      'en-US': 'Inglés',
      'de-DE': 'Alemán',
      'nl-NL': 'Holandés',
      'it-IT': 'Italiano',
      'es-ES': 'Español',
      'sv-SE': 'Sueco',
      'pt-PT': 'Portugués',
      'pl-PL': 'Polaco',
      'ja-JP': 'Japonés',
      'he-IL': 'Hebreo',
      'tr-TR': 'Turco'
    },

    placeholders: {
      'fr-FR': 'Selecciona Francés y comienza a hablar para ver la transcripción aquí...',
      'en-US': 'Selecciona Inglés y comienza a hablar para ver la transcripción aquí...',
      'de-DE': 'Selecciona Alemán y comienza a hablar para ver la transcripción aquí...',
      'nl-NL': 'Selecciona Holandés y comienza a hablar para ver la transcripción aquí...',
      'it-IT': 'Selecciona Italiano y comienza a hablar para ver la transcripción aquí...',
      'es-ES': 'Selecciona Español y comienza a hablar para ver la transcripción aquí...',
      'sv-SE': 'Selecciona Sueco y comienza a hablar para ver la transcripción aquí...',
      'pt-PT': 'Selecciona Portugués y comienza a hablar para ver la transcripción aquí...',
      'pl-PL': 'Selecciona Polaco y comienza a hablar para ver la transcripción aquí...',
      'ja-JP': 'Selecciona Japonés y comienza a hablar para ver la transcripción aquí...',
      'he-IL': 'Selecciona Hebreo y comienza a hablar para ver la transcripción aquí...',
      'tr-TR': 'Selecciona Turco y comienza a hablar para ver la transcripción aquí...'
    },
    
    live: 'En vivo',
    file: 'Archivo',
    
    textCopied: 'Texto copiado al portapapeles',
    cantReachServer: 'No se puede conectar al servidor',
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'Esperando transcripción...',
    
    usageAnalytics: 'Análisis de uso',
    timeRange: 'Rango de tiempo',
    days: 'días',

    dictationModeOn: 'Modo dictado ACTIVO - Dicte la puntuación (ej: "punto", "coma")',
    dictationModeOff: 'Modo dictado INACTIVO - Puntuación generada automáticamente',

    // TTS
    ttsTitle: 'Texto a Voz',
    ttsSubtitle: 'Convertir texto en voz'
  },
  
  'it-IT': {
    welcome: 'Benvenuto in Voxist AI',
    subtitle: 'Piattaforma IA Vocale - ASR & TTS',
    language: 'Lingua',
    
    uploadFile: 'Carica file',
    startRecording: 'Inizia registrazione',
    stopRecording: 'Ferma registrazione',
    processing: 'Elaborazione...',
    
    liveTranscription: 'Trascrizione dal vivo',
    previousTranscriptions: 'Trascrizioni precedenti',
    noTranscriptions: 'Nessuna trascrizione ancora',
    
    uploadAudioFile: 'Carica file audio',
    recordWithMicrophone: 'Registra con microfono',
    playAudio: 'Riproduci audio',
    pause: 'Pausa',
    downloadAudio: 'Scarica audio',
    downloadTranscription: 'Scarica trascrizione',
    delete: 'Elimina',
    decreaseTextSize: 'Riduci dimensione testo',
    increaseTextSize: 'Aumenta dimensione testo',
    copyText: 'Copia testo',
    
    languages: {
      'fr-FR': 'Francese',
      'en-US': 'Inglese',
      'de-DE': 'Tedesco',
      'nl-NL': 'Olandese',
      'it-IT': 'Italiano',
      'es-ES': 'Spagnolo',
      'sv-SE': 'Svedese',
      'pt-PT': 'Portoghese',
      'pl-PL': 'Polacco',
      'ja-JP': 'Giapponese',
      'he-IL': 'Ebraico',
      'tr-TR': 'Turco'
    },

    placeholders: {
      'fr-FR': 'Seleziona Francese e inizia a parlare per vedere la trascrizione qui...',
      'en-US': 'Seleziona Inglese e inizia a parlare per vedere la trascrizione qui...',
      'de-DE': 'Seleziona Tedesco e inizia a parlare per vedere la trascrizione qui...',
      'nl-NL': 'Seleziona Olandese e inizia a parlare per vedere la trascrizione qui...',
      'it-IT': 'Seleziona Italiano e inizia a parlare per vedere la trascrizione qui...',
      'es-ES': 'Seleziona Spagnolo e inizia a parlare per vedere la trascrizione qui...',
      'sv-SE': 'Seleziona Svedese e inizia a parlare per vedere la trascrizione qui...',
      'pt-PT': 'Seleziona Portoghese e inizia a parlare per vedere la trascrizione qui...',
      'pl-PL': 'Seleziona Polacco e inizia a parlare per vedere la trascrizione qui...',
      'ja-JP': 'Seleziona Giapponese e inizia a parlare per vedere la trascrizione qui...',
      'he-IL': 'Seleziona Ebraico e inizia a parlare per vedere la trascrizione qui...',
      'tr-TR': 'Seleziona Turco e inizia a parlare per vedere la trascrizione qui...'
    },
    
    live: 'Dal vivo',
    file: 'File',
    
    textCopied: 'Testo copiato negli appunti',
    cantReachServer: 'Impossibile raggiungere il server',
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'In attesa di trascrizione...',
    
    usageAnalytics: 'Analisi di utilizzo',
    timeRange: 'Intervallo di tempo',
    days: 'giorni',

    dictationModeOn: 'Modalità dettatura ATTIVA - Detta la punteggiatura (es: "punto", "virgola")',
    dictationModeOff: 'Modalità dettatura INATTIVA - Punteggiatura generata automaticamente',

    // TTS
    ttsTitle: 'Sintesi Vocale',
    ttsSubtitle: 'Convertire testo in voce'
  },
  
  'nl-NL': {
    welcome: 'Welkom bij Voxist AI',
    subtitle: 'AI Spraakplatform - ASR & TTS',
    language: 'Taal',
    
    uploadFile: 'Bestand uploaden',
    startRecording: 'Opname starten',
    stopRecording: 'Opname stoppen',
    processing: 'Verwerken...',
    
    liveTranscription: 'Live transcriptie',
    previousTranscriptions: 'Vorige transcripties',
    noTranscriptions: 'Nog geen transcripties',
    
    uploadAudioFile: 'Audio bestand uploaden',
    recordWithMicrophone: 'Opnemen met microfoon',
    playAudio: 'Audio afspelen',
    pause: 'Pauze',
    downloadAudio: 'Audio downloaden',
    downloadTranscription: 'Transcriptie downloaden',
    delete: 'Verwijderen',
    decreaseTextSize: 'Tekstgrootte verkleinen',
    increaseTextSize: 'Tekstgrootte vergroten',
    copyText: 'Tekst kopiëren',
    
    languages: {
      'fr-FR': 'Frans',
      'en-US': 'Engels',
      'de-DE': 'Duits',
      'nl-NL': 'Nederlands',
      'it-IT': 'Italiaans',
      'es-ES': 'Spaans',
      'sv-SE': 'Zweeds',
      'pt-PT': 'Portugees',
      'pl-PL': 'Pools',
      'ja-JP': 'Japans',
      'he-IL': 'Hebreeuws',
      'tr-TR': 'Turks'
    },

    placeholders: {
      'fr-FR': 'Selecteer Frans en begin te spreken om de transcriptie hier te zien...',
      'en-US': 'Selecteer Engels en begin te spreken om de transcriptie hier te zien...',
      'de-DE': 'Selecteer Duits en begin te spreken om de transcriptie hier te zien...',
      'nl-NL': 'Selecteer Nederlands en begin te spreken om de transcriptie hier te zien...',
      'it-IT': 'Selecteer Italiaans en begin te spreken om de transcriptie hier te zien...',
      'es-ES': 'Selecteer Spaans en begin te spreken om de transcriptie hier te zien...',
      'sv-SE': 'Selecteer Zweeds en begin te spreken om de transcriptie hier te zien...',
      'pt-PT': 'Selecteer Portugees en begin te spreken om de transcriptie hier te zien...',
      'pl-PL': 'Selecteer Pools en begin te spreken om de transcriptie hier te zien...',
      'ja-JP': 'Selecteer Japans en begin te spreken om de transcriptie hier te zien...',
      'he-IL': 'Selecteer Hebreeuws en begin te spreken om de transcriptie hier te zien...',
      'tr-TR': 'Selecteer Turks en begin te spreken om de transcriptie hier te zien...'
    },
    
    live: 'Live',
    file: 'Bestand',
    
    textCopied: 'Tekst gekopieerd naar klembord',
    cantReachServer: 'Kan server niet bereiken',
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'Wachten op transcriptie...',
    
    usageAnalytics: 'Gebruiksanalyse',
    timeRange: 'Tijdsperiode',
    days: 'dagen',

    dictationModeOn: 'Dictaatmodus AAN - Spreek leestekens in (bijv. "punt", "komma")',
    dictationModeOff: 'Dictaatmodus UIT - Leestekens automatisch gegenereerd',

    // TTS
    ttsTitle: 'Tekst naar Spraak',
    ttsSubtitle: 'Tekst omzetten naar spraak'
  },
  
  'sv-SE': {
    welcome: 'Välkommen till Voxist AI',
    subtitle: 'AI-röstplattform - ASR & TTS',
    language: 'Språk',
    
    uploadFile: 'Ladda upp fil',
    startRecording: 'Starta inspelning',
    stopRecording: 'Stoppa inspelning',
    processing: 'Behandlar...',
    
    liveTranscription: 'Live transkription',
    previousTranscriptions: 'Tidigare transkriptioner',
    noTranscriptions: 'Inga transkriptioner än',
    
    uploadAudioFile: 'Ladda upp ljudfil',
    recordWithMicrophone: 'Spela in med mikrofon',
    playAudio: 'Spela ljud',
    pause: 'Paus',
    downloadAudio: 'Ladda ner ljud',
    downloadTranscription: 'Ladda ner transkription',
    delete: 'Radera',
    decreaseTextSize: 'Minska textstorlek',
    increaseTextSize: 'Öka textstorlek',
    copyText: 'Kopiera text',
    
    languages: {
      'fr-FR': 'Franska',
      'en-US': 'Engelska',
      'de-DE': 'Tyska',
      'nl-NL': 'Holländska',
      'it-IT': 'Italienska',
      'es-ES': 'Spanska',
      'sv-SE': 'Svenska',
      'pt-PT': 'Portugisiska',
      'pl-PL': 'Polska',
      'ja-JP': 'Japanska',
      'he-IL': 'Hebreiska',
      'tr-TR': 'Turkiska'
    },

    placeholders: {
      'fr-FR': 'Välj franska och börja tala för att se transkriptionen här...',
      'en-US': 'Välj engelska och börja tala för att se transkriptionen här...',
      'de-DE': 'Välj tyska och börja tala för att se transkriptionen här...',
      'nl-NL': 'Välj holländska och börja tala för att se transkriptionen här...',
      'it-IT': 'Välj italienska och börja tala för att se transkriptionen här...',
      'es-ES': 'Välj spanska och börja tala för att se transkriptionen här...',
      'sv-SE': 'Välj svenska och börja tala för att se transkriptionen här...',
      'pt-PT': 'Välj portugisiska och börja tala för att se transkriptionen här...',
      'pl-PL': 'Välj Polska och börja tala för att se transkriptionen här...',
      'ja-JP': 'Välj Japanska och börja tala för att se transkriptionen här...',
      'he-IL': 'Välj Hebreiska och börja tala för att se transkriptionen här...',
      'tr-TR': 'Välj Turkiska och börja tala för att se transkriptionen här...'
    },
    
    live: 'Live',
    file: 'Fil',
    
    textCopied: 'Text kopierad till urklipp',
    cantReachServer: 'Kan inte nå servern',
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'Väntar på transkription...',
    
    usageAnalytics: 'Användningsanalys',
    timeRange: 'Tidsperiod',
    days: 'dagar',

    dictationModeOn: 'Dikteringsläge PÅ - Säg skiljetecken (t.ex. "punkt", "komma")',
    dictationModeOff: 'Dikteringsläge AV - Skiljetecken genereras automatiskt',

    // TTS
    ttsTitle: 'Text till Tal',
    ttsSubtitle: 'Konvertera text till tal'
  },
  
  'pt-PT': {
    welcome: 'Bem-vindo ao Voxist AI',
    subtitle: 'Plataforma IA de Voz - ASR & TTS',
    language: 'Idioma',
    
    uploadFile: 'Carregar ficheiro',
    startRecording: 'Iniciar gravação',
    stopRecording: 'Parar gravação',
    processing: 'A processar...',
    
    liveTranscription: 'Transcrição ao vivo',
    previousTranscriptions: 'Transcrições anteriores',
    noTranscriptions: 'Ainda não há transcrições',
    
    uploadAudioFile: 'Carregar ficheiro de áudio',
    recordWithMicrophone: 'Gravar com microfone',
    playAudio: 'Reproduzir áudio',
    pause: 'Pausa',
    downloadAudio: 'Descarregar áudio',
    downloadTranscription: 'Descarregar transcrição',
    delete: 'Eliminar',
    decreaseTextSize: 'Diminuir tamanho do texto',
    increaseTextSize: 'Aumentar tamanho do texto',
    copyText: 'Copiar texto',
    
    languages: {
      'fr-FR': 'Francês',
      'en-US': 'Inglês',
      'de-DE': 'Alemão',
      'nl-NL': 'Holandês',
      'it-IT': 'Italiano',
      'es-ES': 'Espanhol',
      'sv-SE': 'Sueco',
      'pt-PT': 'Português',
      'pl-PL': 'Polaco',
      'ja-JP': 'Japonês',
      'he-IL': 'Hebraico',
      'tr-TR': 'Turco'
    },

    placeholders: {
      'fr-FR': 'Selecione Francês e comece a falar para ver a transcrição aqui...',
      'en-US': 'Selecione Inglês e comece a falar para ver a transcrição aqui...',
      'de-DE': 'Selecione Alemão e comece a falar para ver a transcrição aqui...',
      'nl-NL': 'Selecione Holandês e comece a falar para ver a transcrição aqui...',
      'it-IT': 'Selecione Italiano e comece a falar para ver a transcrição aqui...',
      'es-ES': 'Selecione Espanhol e comece a falar para ver a transcrição aqui...',
      'sv-SE': 'Selecione Sueco e comece a falar para ver a transcrição aqui...',
      'pt-PT': 'Selecione Português e comece a falar para ver a transcrição aqui...',
      'pl-PL': 'Selecione Polaco e comece a falar para ver a transcrição aqui...',
      'ja-JP': 'Selecione Japonês e comece a falar para ver a transcrição aqui...',
      'he-IL': 'Selecione Hebraico e comece a falar para ver a transcrição aqui...',
      'tr-TR': 'Selecione Turco e comece a falar para ver a transcrição aqui...'
    },
    
    live: 'Ao vivo',
    file: 'Ficheiro',
    
    textCopied: 'Texto copiado para a área de transferência',
    cantReachServer: 'Não é possível contactar o servidor',
    
    teleprompter: 'Teleprompter',
    waitingForTranscription: 'À espera de transcrição...',
    
    usageAnalytics: 'Análise de uso',
    timeRange: 'Intervalo de tempo',
    days: 'dias',

    dictationModeOn: 'Modo ditado ATIVO - Dite a pontuação (ex: "ponto", "vírgula")',
    dictationModeOff: 'Modo ditado INATIVO - Pontuação gerada automaticamente',

    // TTS
    ttsTitle: 'Texto para Fala',
    ttsSubtitle: 'Converter texto em fala'
  }
};

/**
 * Maps ASR language codes to UI language codes.
 */
function resolveUILanguage(language: string): string {
  if (translations[language]) return language;
  return 'en-US';
}

export function getTranslations(language: string): Translations {
  return translations[resolveUILanguage(language)] || translations['en-US'];
}

export function getUILanguage(language: string): string {
  return resolveUILanguage(language);
}